// Facturas y boletas desde el DTE (GET /ventas/documentos-emitidos, 2026-10-26): el documento manda y
// la nota de venta es su origen.
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';

export type OrigenDocumento =
  | { tipo: 'NOTA'; notaId: string; numeroVisible: string; canal: string }
  | { tipo: 'VENTA_DIRECTA'; notaId: string; canal: string }
  | {
      tipo: 'AJUSTE';
      notaId: string;
      numeroVisible: string;
      ajusteId: string;
      referido: { documentoId: string; tipoDte: number; folio: string | null } | null;
    }
  | null;

export interface DocumentoEmitido {
  documentoId: string;
  tipoDte: number;
  folio: string | null;
  fechaEmision: string | null;
  receptorRut: string | null;
  receptorRazonSocial: string | null;
  clienteEntidadId: string | null;
  total: string;
  estado: string;
  estadoEnvio: string | null;
  estadoSii: string | null;
  origen: OrigenDocumento;
}

export interface LineaDte {
  nro: number;
  codigo: string;
  nombre: string;
  cantidad: string;
  unidad: string;
  precioUnitario: string;
  montoItem: string;
}

export interface FichaDocumento extends DocumentoEmitido {
  trackId: string | null;
  creadoEn: string;
  contenido: {
    receptor: {
      rut: string;
      razonSocial: string;
      giro: string | null;
      direccion: string | null;
      comuna: string | null;
    } | null;
    lineas: LineaDte[];
    totales: {
      neto: string;
      exento: string;
      iva: string;
      total: string;
      impuestosAdicionales?: Array<{ codigo?: string | number; monto: string; nombre?: string }>;
    } | null;
    referencias: Array<{
      nro: number;
      tipoDocumento: string;
      folio: string;
      fecha: string;
      codigo: 1 | 2 | 3 | null;
      razon: string | null;
    }>;
    impresion: {
      vendedor: string | null;
      condicion: string | null;
      fechaVencimiento: string | null;
      observaciones: string | null;
    } | null;
    medioPago: number | null;
    formaPago: number | null;
  };
  ajustes: Array<{
    ajusteId: string;
    tipo: string;
    clase: string;
    estado: string;
    bruto: string;
    descripcion: string;
    creadoEn: string;
    documentoId: string | null;
    tipoDte: number | null;
    folio: string | null;
    documentoEstado: string | null;
  }>;
}

export interface FiltroDocumentos {
  desde?: string | undefined;
  hasta?: string | undefined;
  tipoDte?: string | undefined;
  folio?: string | undefined;
  cliente?: string | undefined;
  clienteEntidadId?: string | undefined;
  notaId?: string | undefined;
  /** Varias notas, separadas por coma (los cobros de un turno). */
  notaIds?: string | undefined;
  pagina?: number | undefined;
}

export const clavesDocumentos = {
  todos: (e: string) => ['emp', e, 'documentos-emitidos'] as const,
};

export function useDocumentosEmitidos(empresaId: string, f: FiltroDocumentos, habilitado = true) {
  return useQuery({
    queryKey: [...clavesDocumentos.todos(empresaId), 'lista', f],
    queryFn: () =>
      api.get<{ items: DocumentoEmitido[]; hayMas: boolean }>('/ventas/documentos-emitidos', {
        query: { empresaId, ...f },
      }),
    enabled: habilitado,
    staleTime: 10_000,
  });
}

export function useFichaDocumento(empresaId: string, documentoId: string) {
  return useQuery({
    queryKey: [...clavesDocumentos.todos(empresaId), 'ficha', documentoId],
    queryFn: () => api.get<FichaDocumento>(`/ventas/documentos-emitidos/${documentoId}`),
    // Mientras el SII no responde, se refresca sola.
    refetchInterval: (q) =>
      q.state.data &&
      !['ACEPTADO', 'ACEPTADO_CON_REPAROS', 'RECHAZADO'].includes(q.state.data.estadoSii ?? '')
        ? 15_000
        : false,
  });
}

const ESTADO_SII: Record<string, string> = {
  ACEPTADO: 'Aceptado SII',
  ACEPTADO_CON_REPAROS: 'Aceptado con reparos',
  RECHAZADO: 'Rechazado SII',
  PROCESANDO: 'En revisión SII',
  TRACKID_ASIGNADO: 'Enviado al SII',
};
/** El estado en palabras: lo que importa es si el SII lo aceptó. */
export function textoEstado(d: Pick<DocumentoEmitido, 'estado' | 'estadoEnvio' | 'estadoSii'>) {
  if (d.estado === 'ANULADO_SIN_EMISION' || d.estado === 'ANULADO_PRE_ENVIO') return 'Anulado';
  if (d.estado === 'FALLIDO') return 'Falló la emisión';
  if (d.estado === 'SOLICITADO') return 'Emitiendo…';
  if (d.estadoSii && ESTADO_SII[d.estadoSii]) return ESTADO_SII[d.estadoSii]!;
  if (d.estadoEnvio === 'PENDIENTE') return 'Por enviar al SII';
  return (d.estadoSii ?? d.estado).toLowerCase().replace(/_/g, ' ');
}
export function tonoEstado(d: Pick<DocumentoEmitido, 'estado' | 'estadoSii'>) {
  if (d.estado === 'FALLIDO' || d.estadoSii === 'RECHAZADO') return 'tono-malo';
  if (d.estadoSii === 'ACEPTADO') return 'tono-ok';
  if (d.estado.startsWith('ANULADO')) return 'tono-neutro';
  return 'tono-aviso';
}
